/**
 * Performance Dashboard Component
 * 
 * Displays real-time performance metrics and Core Web Vitals
 * for monitoring application performance in development and production.
 */

import React, { useState, useEffect } from 'react';
import { Activity, Zap, Clock, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { 
  usePerformanceMonitoring, 
  PERFORMANCE_THRESHOLDS, 
  classifyMetric,
  exportPerformanceData
} from '../../utils/performanceMonitoring';

interface MetricCardProps {
  title: string;
  value: number | undefined;
  unit: string;
  threshold: { good: number; poor: number };
  icon: React.ReactNode;
  description: string;
}

function MetricCard({ title, value, unit, threshold, icon, description }: MetricCardProps) {
  if (value === undefined) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</h3>
          <div className="text-gray-400">{icon}</div>
        </div>
        <div className="text-2xl font-bold text-gray-400 mb-1">-</div>
        <p className="text-xs text-gray-500 dark:text-gray-400">Not measured yet</p>
      </div>
    );
  }

  const rating = classifyMetric(value, threshold);
  const colorClasses = {
    good: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    'needs-improvement': 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    poor: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
  };

  const iconColors = {
    good: 'text-green-500',
    'needs-improvement': 'text-yellow-500',
    poor: 'text-red-500'
  };

  return (
    <div className={`rounded-lg p-4 border ${colorClasses[rating]}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium">{title}</h3>
        <div className={iconColors[rating]}>{icon}</div>
      </div>
      <div className="text-2xl font-bold mb-1">
        {value.toFixed(value < 1 ? 3 : 0)}{unit}
      </div>
      <div className="flex items-center justify-between">
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
          rating === 'good' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
          rating === 'needs-improvement' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
          'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
        }`}>
          {rating === 'good' ? 'Good' : rating === 'needs-improvement' ? 'Needs Work' : 'Poor'}
        </span>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">{description}</p>
    </div>
  );
}

interface PerformanceScoreProps {
  score: number;
}

function PerformanceScore({ score }: PerformanceScoreProps) {
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400';
    if (score >= 70) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBackground = (score: number) => {
    if (score >= 90) return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
    if (score >= 70) return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
    return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 90) return <TrendingUp className="w-6 h-6 text-green-500" />;
    if (score >= 70) return <Activity className="w-6 h-6 text-yellow-500" />;
    return <TrendingDown className="w-6 h-6 text-red-500" />;
  };

  return (
    <div className={`rounded-lg p-6 border ${getScoreBackground(score)}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Score</h2>
        {getScoreIcon(score)}
      </div>
      <div className={`text-4xl font-bold mb-2 ${getScoreColor(score)}`}>
        {score}/100
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {score >= 90 ? 'Excellent performance!' :
         score >= 70 ? 'Good performance with room for improvement' :
         'Performance needs optimization'}
      </p>
    </div>
  );
}

interface BundleInfoProps {
  bundleMetrics: any[];
}

function BundleInfo({ bundleMetrics }: BundleInfoProps) {
  const latestBundle = bundleMetrics[bundleMetrics.length - 1];
  
  if (!latestBundle) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bundle Information</h3>
        <p className="text-gray-500 dark:text-gray-400">Bundle metrics not available yet</p>
      </div>
    );
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const bundleRating = classifyMetric(latestBundle.totalSize, PERFORMANCE_THRESHOLDS.BUNDLE_SIZE);
  const bundleColorClass = bundleRating === 'good' ? 'text-green-600 dark:text-green-400' :
                          bundleRating === 'needs-improvement' ? 'text-yellow-600 dark:text-yellow-400' :
                          'text-red-600 dark:text-red-400';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bundle Information</h3>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Size</p>
          <p className={`text-xl font-bold ${bundleColorClass}`}>
            {formatSize(latestBundle.totalSize)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Load Time</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            {(latestBundle.loadTime / 1000).toFixed(2)}s
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Cache Hit Rate</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            {(latestBundle.cacheHitRate * 100).toFixed(1)}%
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Chunks</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            {Object.keys(latestBundle.chunkSizes).length}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Largest Chunks</h4>
        {Object.entries(latestBundle.chunkSizes)
          .sort(([,a], [,b]) => (b as number) - (a as number))
          .slice(0, 5)
          .map(([name, size]) => (
            <div key={name} className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400 truncate mr-2">{name}</span>
              <span className="text-gray-900 dark:text-white font-medium">
                {formatSize(size as number)}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

export default function PerformanceDashboard() {
  const { currentMetrics, performanceScore } = usePerformanceMonitoring();
  const [isVisible, setIsVisible] = useState(false);
  const [exportData, setExportData] = useState<any>(null);

  // Only show in development or when explicitly enabled
  useEffect(() => {
    const shouldShow = process.env.NODE_ENV === 'development' || 
                      localStorage.getItem('show-performance-dashboard') === 'true';
    setIsVisible(shouldShow);
  }, []);

  const handleExportData = () => {
    const data = exportPerformanceData();
    setExportData(data);
    
    // Download as JSON
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 max-w-4xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            Performance Dashboard
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleExportData}
              className="text-sm px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
            >
              Export Data
            </button>
            <button
              onClick={() => setIsVisible(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ×
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          <PerformanceScore score={performanceScore} />
          
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard
              title="LCP"
              value={currentMetrics?.lcp}
              unit="ms"
              threshold={PERFORMANCE_THRESHOLDS.LCP}
              icon={<Clock className="w-4 h-4" />}
              description="Largest Contentful Paint - Time to render largest element"
            />
            
            <MetricCard
              title="INP"
              value={currentMetrics?.inp}
              unit="ms"
              threshold={PERFORMANCE_THRESHOLDS.INP}
              icon={<Zap className="w-4 h-4" />}
              description="Interaction to Next Paint - Responsiveness to user input"
            />
            
            <MetricCard
              title="CLS"
              value={currentMetrics?.cls}
              unit=""
              threshold={PERFORMANCE_THRESHOLDS.CLS}
              icon={<AlertTriangle className="w-4 h-4" />}
              description="Cumulative Layout Shift - Visual stability"
            />
            
            <MetricCard
              title="FCP"
              value={currentMetrics?.fcp}
              unit="ms"
              threshold={PERFORMANCE_THRESHOLDS.FCP}
              icon={<TrendingUp className="w-4 h-4" />}
              description="First Contentful Paint - Time to first content"
            />
            
            <MetricCard
              title="TTFB"
              value={currentMetrics?.ttfb}
              unit="ms"
              threshold={PERFORMANCE_THRESHOLDS.TTFB}
              icon={<Activity className="w-4 h-4" />}
              description="Time to First Byte - Server response time"
            />
            
            {currentMetrics?.fid && (
              <MetricCard
                title="FID"
                value={currentMetrics.fid}
                unit="ms"
                threshold={PERFORMANCE_THRESHOLDS.FID}
                icon={<Zap className="w-4 h-4" />}
                description="First Input Delay - Legacy responsiveness metric"
              />
            )}
          </div>
        </div>

        <BundleInfo bundleMetrics={exportData?.bundleMetrics || []} />
        
        {currentMetrics && (
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Last updated: {new Date(currentMetrics.timestamp).toLocaleTimeString()}
          </div>
        )}
      </div>
    </div>
  );
}